import { popoverMotion } from "../../../design-system/tokens/popoverMotion";
import styled from "styled-components";
import { hideScrollbar } from "../../molecules/Dropdown/Dropdown.styles";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderWidth,
  borderStyle,
  borderRadius,
} from "../../../design-system/tokens/borders";
import { fontSizes, fontWeights, typographyMixin } from "../../../design-system/tokens/typography";
import { shadows } from "@magicbi/ui-library";
import { mediaQueries } from "../../../design-system/tokens/breakpoints";
import { TRIGGER_BADGE_FONT_SIZE, TRIGGER_BADGE_SIZE } from "./WorkspaceSelectorDropdown.constants";

export const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

/** Closed-state pill: 32px tall, full radius, stroke[200] border, 13/600 strong text. */
export const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  height: ${spacing[8]};
  padding: 0 ${spacing[3]};
  background: ${rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.full};
  cursor: pointer;
  font-size: ${fontSizes[13]};
  font-weight: ${fontWeights.semibold};
  color: ${rawColors.text.strongEmphasis};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${rawColors.stroke.strong};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[500]};
    outline-offset: 2px;
  }

  /* Phone top bar: tighter pill, the name truncates sooner. */
  ${mediaQueries.down.xs} {
    gap: ${spacing[1]};
    padding: 0 ${spacing[2]};
  }
`;

/** 18px square initial badge (radius 5, 10/700). */
export const TriggerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${TRIGGER_BADGE_SIZE};
  height: ${TRIGGER_BADGE_SIZE};
  flex-shrink: 0;
  border-radius: ${borderRadius[5]};
  background: ${rawColors.primary[100]};
  color: ${rawColors.primary[500]};
  font-size: ${TRIGGER_BADGE_FONT_SIZE};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
`;

export const TriggerValue = styled.span`
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;

  ${mediaQueries.down.xs} {
    max-width: 4rem;
  }
`;

/** Chevron rotates 180° when open. */
export const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${rawColors.text.lowEmphasis};
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});

  & > svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/**
 * Menu opens DOWNWARD from the trigger (header lives at the top of the page).
 * Right-aligned to the trigger so it doesn't overflow off the right edge.
 */
export const Menu = styled.div<{ $closing?: boolean }>`
  position: absolute;
  ${({ $closing }) => popoverMotion($closing)}
  transform-origin: top right;
  top: calc(100% + ${spacing[1]});
  right: 0;
  min-width: 240px;
  max-width: 320px;
  max-height: 280px;
  overflow-y: auto;
  ${hideScrollbar}
  /* Same surface as the Help menu: 16px radius, modal shadow, 6px inset, 40px rows. */
  box-sizing: border-box;
  background: ${rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius["2xl"]};
  box-shadow: ${shadows.component.modal};
  padding: ${spacing[1.5]};
  z-index: 1000;
`;

export const WorkspaceRow = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  min-height: ${spacing[10]};
  padding: 0 ${spacing[3]};
  background: ${({ $isSelected }) =>
    $isSelected ? rawColors.primary[50] : "transparent"};
  border: 0;
  border-radius: ${borderRadius.xl};
  cursor: pointer;
  text-align: left;
  font-size: ${fontSizes[13]};
  color: ${rawColors.text.highEmphasis};

  &:hover,
  &:focus-visible {
    background: ${rawColors.primary[50]};
  }

  &:focus-visible {
    outline: none;
  }
`;

export const WorkspaceRowName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${rawColors.primary[500]};

  & > svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

export const EmptyState = styled.div`
  padding: ${spacing[3]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  text-align: center;
`;

export const MenuDivider = styled.div`
  height: 1px;
  background: ${rawColors.stroke[200]};
  margin: ${spacing[1]} 0;
`;

/** "Manage Workspace" footer row — same visual rhythm as workspace rows. */
export const ManageRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  min-height: ${spacing[10]};
  padding: 0 ${spacing[3]};
  background: transparent;
  border: 0;
  border-radius: ${borderRadius.xl};
  cursor: pointer;
  text-align: left;
  font-size: ${fontSizes[13]};
  color: ${rawColors.text.highEmphasis};

  & svg {
    color: ${rawColors.text.mediumEmphasis};
  }

  &:hover,
  &:focus-visible {
    background: ${rawColors.primary[50]};
  }

  &:focus-visible {
    outline: none;
  }
`;
